import { IBranch } from '@/interfaces/IBranch';
import { IRestaurant } from '@/interfaces/IRestaurant';
import { getEffectiveTier } from '@/utils/billing-entitlements.utils';

export type TMenuQrTargetKind = 'storefront' | 'takeaway' | 'table';
export type TMenuQrDesign = 'view' | 'orders';
export type TMenuQrDesignMode = 'view' | 'auto' | 'orders';

/** The branch/plan flags that decide whether a scanned QR can place orders. */
export interface IMenuQrOrderingContext {
  effectiveTier: number;
  allowOrders: boolean;
  tableOrders: boolean;
  takeAway: boolean;
  takeAwayOnlyQr: boolean;
  delivery: boolean;
}

export type TMenuQrRestaurantLike = Pick<
  IRestaurant,
  'paidTier' | 'planExpiresAt'
>;
export type TMenuQrBranchLike = Pick<
  IBranch,
  'allowOrders' | 'tableOrders' | 'takeAway' | 'takeAwayOnlyQr' | 'delivery'
>;

export function toMenuQrOrderingContext(
  restaurant: TMenuQrRestaurantLike | null,
  branch: TMenuQrBranchLike | null
): IMenuQrOrderingContext {
  return {
    effectiveTier: restaurant ? getEffectiveTier(restaurant) : 0,
    allowOrders: !!branch?.allowOrders,
    tableOrders: !!branch?.tableOrders,
    takeAway: !!branch?.takeAway,
    takeAwayOnlyQr: !!branch?.takeAwayOnlyQr,
    delivery: !!branch?.delivery,
  };
}

/**
 * Mirrors the customer app's order-type availability (shared/utils/menu.utils.ts)
 * for each kind of QR: tier 0 or orders disabled → nothing; table QR → table
 * orders; take-away QR → take-away; storefront QR → take-away (unless it is
 * restricted to take-away QRs) or delivery.
 */
export function isMenuQrOrderingEnabled(
  kind: TMenuQrTargetKind,
  ctx: IMenuQrOrderingContext
): boolean {
  if (ctx.effectiveTier <= 0 || !ctx.allowOrders) return false;

  if (kind === 'table') return ctx.tableOrders;
  if (kind === 'takeaway') return ctx.takeAway;

  return (ctx.takeAway && !ctx.takeAwayOnlyQr) || ctx.delivery;
}

export function resolveMenuQrDesign(
  mode: TMenuQrDesignMode,
  kind: TMenuQrTargetKind,
  ctx: IMenuQrOrderingContext
): TMenuQrDesign {
  if (mode === 'view') return 'view';
  if (mode === 'orders') return 'orders';

  return isMenuQrOrderingEnabled(kind, ctx) ? 'orders' : 'view';
}
